import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DatePicker, { registerLocale } from "react-datepicker";
import { ko } from "date-fns/locale";

import MobileHeader from "../../component/MobileHeader";
import "../../css/common.css";
import "../../css/diagnostic/diagnosticEdit.css";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ko", ko);

/** 층 선택 옵션 */
const floorOptions = [
    "",
    "1층",
    "2층",
    "3층",
    "4층",
    "5층",
    "6층",
    "7층",
    "8층",
    "9층",
    "10층",
];

/** 직무 코드 → 한글명 */
const DUTY_LABEL = {
    MANAGER: "관리",
    SUPERVISOR: "작업지휘자",
    INSPECTOR: "입회자",
    SIGNALMAN: "신호수",
    GUIDER: "유도자",
    CONTROLLER: "통제자",
    FIRE_WATCHER: "화기감시자",
    CHEMICAL_A_WORKER: "가스·케미칼 A급 위험작업 작업자",
    CONFINED_SPACE_WORKER: "밀폐공간 작업 참여자",
    OTHER: "기타",
};

/** 폼 초기값 */
const initialForm = {
    subcontractorIdx: "",
    subcontractorNM: "",

    // 기술인
    name: "",
    subcontractorEngnrIdx: "",
    subcontractorEngnrNM: "",

    // 작업지휘자
    supervisorName: "",
    supervisorIdx: "",
    supervisorNM: "",

    lineName: "",
    floor: "",
    detailPlace: "",
    caughtDT: "",

    evlIdx: "",
    evlClass: "",
    evlCategory: "",
    increasePT: "",
    deductedPT: "",
    totalIncreasePT: "",
    totalDeductedPT: "",

    equipmentName: "",
    actionDescription: "",
    caughtNote: "",
};

export default function DiagnosticEdit({ onPrev }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const diagIdx = searchParams.get("diagIdx");
    const API = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("accessToken") || "";

    /** 공통 헤더 */
    const headers = { Authorization: token };

    /** 전체 페이지 로딩 */
    const [loadingPage, setLoadingPage] = useState(true);

    /** 저장 버튼 로딩 */
    const [saving, setSaving] = useState(false);

    /** 분류 / 내용 / 세부사항 점수 로딩 */
    const [loading, setLoading] = useState({
        classes: false,
        categories: false,
        points: false,
    });

    /** 로그인 사용자 정보 */
    const [userInfo, setUserInfo] = useState({
        regId: "",
        regNM: "",
        aboveIdx: "",
        aboveNM: "",
        fldIdx: "",
        fldNM: "",
    });

    /** 업체 목록 */
    const [companyList, setCompanyList] = useState([]);

    /** 라인 목록 */
    const [lines, setLines] = useState([]);

    /** 기술인 검색 결과 */
    const [searchResults, setSearchResults] = useState([]);

    /** 기술인 검색 중 여부 */
    const [searchLoading, setSearchLoading] = useState(false);

    /** 작업지휘자 검색 결과 */
    const [supervisorResults, setSupervisorResults] = useState([]);

    /** 작업지휘자 검색 중 여부 */
    const [supervisorSearchLoading, setSupervisorSearchLoading] = useState(false);

    /** 평가 대분류 목록 */
    const [evaluationClassList, setEvaluationClassList] = useState([]);

    /** 대분류별 세부사항 목록 */
    const [evaluationCategoryByClass, setEvaluationCategoryByClass] = useState({});

    /** 세부사항별 점수 */
    const [pointByCategoryIdx, setPointByCategoryIdx] = useState({});

    /** 실제 폼 데이터 */
    const [form, setForm] = useState(initialForm);

    /** 현재 우수인지 불합리인지 판별 */
    const isPlus = form.evlIdx === "17";
    const isMinus = form.evlIdx === "18";

    /** 분류에 따라 클래스 조회 URL 결정 */
    const classEndpoint = isPlus
        ? `${API}/evaluationClassPlusList`
        : isMinus
            ? `${API}/evaluationClassMinusList`
            : "";

    /** 분류에 따라 점수 조회 URL 결정 */
    const pointEndpoint = isPlus
        ? `${API}/evaluationPlusPoint`
        : isMinus
            ? `${API}/evaluationMinusPoint`
            : "";

    /** form의 특정 key 값을 변경하는 공통 함수 */
    const handleChange = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    /** 문자열 날짜를 Date 객체로 변환 */
    const parseDateTimeLocal = (value) => {
        if (!value) return null;
        return new Date(value);
    };

    /** Date 객체를 yyyy-MM-ddTHH:mm 형식으로 변환 */
    const formatDateTimeLocal = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    /** 서버 전송 전에 날짜 문자열을 T 형식으로 맞춤 */
    const toLocalDateTimeString = (value) => {
        if (!value) return "";
        return String(value).includes("T")
            ? value
            : String(value).replace(" ", "T");
    };

    /** 직무 코드 → 표시용 한글명 */
    const dutyLabel = (value) => {
        return DUTY_LABEL[(value || "").toUpperCase()] || "기타";
    };

    /** 생년월일 표시 */
    const formatBirth = (value) => {
        return value || "-";
    };

    /** 점검 위치 문자열 생성 */
    const makeCaughtPlace = () => {
        return [form.lineName, form.floor, form.detailPlace]
            .filter(Boolean)
            .join(" / ");
    };

    /** 분류 변경 시 하위 값들 초기화 */
    const handleEvlIdxChange = (value) => {
        setForm((prev) => ({
            ...prev,
            evlIdx: value,
            evlClass: "",
            evlCategory: "",
            increasePT: "",
            deductedPT: "",
            totalIncreasePT: "",
            totalDeductedPT: "",
        }));

        setEvaluationClassList([]);
        setEvaluationCategoryByClass({});
        setPointByCategoryIdx({});
    };

    /** 내용 변경 시 세부사항/점수 초기화 */
    const handleEvalClassChange = (value) => {
        setForm((prev) => ({
            ...prev,
            evlClass: value,
            evlCategory: "",
            increasePT: "",
            deductedPT: "",
            totalIncreasePT: "",
            totalDeductedPT: "",
        }));
    };

    /** 세부사항 선택 시 점수 자동 반영 */
    const handleEvalCategoryChange = (value) => {
        const rawPoint = pointByCategoryIdx[Number(value)];
        const point = rawPoint == null ? 0 : Math.abs(Number(rawPoint));

        setForm((prev) => ({
            ...prev,
            evlCategory: value,
            increasePT: prev.evlIdx === "17" ? point : "",
            deductedPT: prev.evlIdx === "18" ? point : "",
            totalIncreasePT: prev.evlIdx === "17" ? point : "",
            totalDeductedPT: prev.evlIdx === "18" ? point : "",
        }));
    };

    /** 업체 변경 시 관련 값 초기화 */
    const handleCompanyChange = (underIdx) => {
        const selected = companyList.find(
            (company) => String(company.underIdx) === String(underIdx)
        );

        setForm((prev) => ({
            ...prev,
            subcontractorIdx: String(underIdx || ""),
            subcontractorNM: selected?.underNM || "",

            name: "",
            subcontractorEngnrIdx: "",
            subcontractorEngnrNM: "",

            supervisorName: "",
            supervisorIdx: "",
            supervisorNM: "",
        }));

        setSearchResults([]);
        setSupervisorResults([]);
    };

    /** 기술인 이름 검색 */
    const searchPersonsByName = () => {
        const keyword = form.name.trim();

        if (!form.subcontractorIdx) {
            alert("업체를 먼저 선택해주세요.");
            return;
        }

        if (!userInfo.fldIdx) {
            alert("현장 정보를 불러오지 못했습니다.");
            return;
        }

        if (!keyword) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);

        axios
            .get(`${API}/person/search`, {
                params: {
                    engnrNM: keyword,
                    engnrFldIdx: userInfo.fldIdx,
                    engnrAffl: Number(form.subcontractorIdx),
                },
                headers,
            })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setSearchResults(list);

                if (list.length === 0) {
                    alert("기술인을 찾을 수 없습니다.");
                }
            })
            .catch((err) => {
                console.error(
                    "person search error:",
                    err?.response?.status,
                    err?.response?.data
                );
                setSearchResults([]);
                alert("기술인을 찾을 수 없습니다.");
            })
            .finally(() => {
                setSearchLoading(false);
            });
    };

    const searchSupervisorsByName = () => {
        const keyword = form.supervisorName.trim();

        if (!form.subcontractorIdx) {
            alert("업체를 먼저 선택해주세요.");
            return;
        }

        if (!userInfo.fldIdx) {
            alert("현장 정보를 불러오지 못했습니다.");
            return;
        }

        if (!keyword) {
            setSupervisorResults([]);
            return;
        }

        setSupervisorSearchLoading(true);

        axios
            .get(`${API}/supervisor/search`, {
                params: {
                    engnrNM: keyword,
                    engnrFldIdx: userInfo.fldIdx,
                    engnrAffl: Number(form.subcontractorIdx),
                },
                headers,
            })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                setSupervisorResults(list);

                if (list.length === 0) {
                    alert("작업지휘자를 찾을 수 없습니다.");
                }
            })
            .catch((err) => {
                console.error(
                    "supervisor search error:",
                    err?.response?.status,
                    err?.response?.data
                );
                setSupervisorResults([]);
                alert("작업지휘자를 찾을 수 없습니다.");
            })
            .finally(() => {
                setSupervisorSearchLoading(false);
            });
    };

    /** 필수 입력값 검사 */
    const validate = () => {
        if (!form.subcontractorIdx) return alert("업체를 선택해주세요."), false;

        const hasEngineerName = !!form.name.trim();
        const hasEngineerSelected = !!form.subcontractorEngnrIdx;

        const hasSupervisorName = !!form.supervisorName.trim();
        const hasSupervisorSelected = !!form.supervisorIdx;

        if (!hasEngineerName && !hasSupervisorName) {
            return alert("기술인 또는 작업지휘자 중 한 명은 입력해주세요."), false;
        }

        if (hasEngineerName && !hasEngineerSelected) {
            return alert("기술인은 검색 후 선택해주세요."), false;
        }

        if (hasSupervisorName && !hasSupervisorSelected) {
            return alert("작업지휘자는 검색 후 선택해주세요."), false;
        }

        if (!form.lineName) return alert("점검 라인을 선택해주세요."), false;
        if (!form.detailPlace.trim()) return alert("세부 위치를 입력해주세요."), false;
        if (!form.floor) return alert("층을 선택해주세요."), false;
        if (!form.caughtDT) return alert("발생 일시를 입력해주세요."), false;
        if (!form.evlIdx) return alert("분류를 선택해주세요."), false;
        if (!form.evlClass) return alert("내용을 선택해주세요."), false;
        if (!form.evlCategory) return alert("세부사항을 선택해주세요."), false;
        if (!form.equipmentName.trim()) return alert("설비명을 입력해주세요."), false;
        if (!form.actionDescription.trim()) return alert("조치내용을 입력해주세요."), false;
        if (!form.caughtNote.trim()) return alert("상세내용을 입력해주세요."), false;

        return true;
    };

    /** 수정 저장 */
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;

        if (!window.confirm("수정하시겠습니까?")) return;

        setSaving(true);

        axios
            .post(
                `${API}/api/diagnostic/update?diagIdx=${diagIdx}`,
                {
                    evlStatus: isPlus ? "IND_PLUS" : "IND_MINUS",
                    fldIdx: String(userInfo.fldIdx || ""),
                    fldNM: String(userInfo.fldNM || ""),
                    subcontractorIdx: String(form.subcontractorIdx || ""),
                    subcontractorNM: String(form.subcontractorNM || ""),
                    caughtDT: toLocalDateTimeString(form.caughtDT),
                    caughtPlace: makeCaughtPlace(),
                    caughtNote: String(form.caughtNote || ""),
                    evlIdx: String(form.evlIdx || ""),
                    evlClass: String(form.evlClass || ""),
                    evlCategory: String(form.evlCategory || ""),
                    deductedPT: Number(form.deductedPT || 0),
                    increasePT: Number(form.increasePT || 0),
                    totalDeductedPT: Number(form.totalDeductedPT || 0),
                    totalIncreasePT: Number(form.totalIncreasePT || 0),
                    regId: String(userInfo.regId || ""),
                    regNM: String(userInfo.regNM || ""),

                    subcontractorEngnrIdx: String(form.subcontractorEngnrIdx || ""),
                    subcontractorEngnrNM: String(
                        form.subcontractorEngnrNM || form.name || ""
                    ),

                    supervisorIdx: String(form.supervisorIdx || ""),
                    supervisorName: String(form.supervisorName || ""),

                    aboveIdx: String(userInfo.aboveIdx || ""),
                    aboveNM: String(userInfo.aboveNM || ""),

                    equipmentName: String(form.equipmentName || ""),
                    actionDescription: String(form.actionDescription || ""),
                },
                {
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                    },
                }
            )
            .then(() => {
                alert("수정이 완료되었습니다.");
                navigate(
                    `/mylist?regId=${encodeURIComponent(userInfo.regId || "")}&createdAt=${encodeURIComponent(
                        form.caughtDT ? String(form.caughtDT).split("T")[0] : ""
                    )}`
                );
            })
            .catch((err) => {
                console.error(
                    "updateDiagnostic error:",
                    err?.response?.status,
                    err?.response?.data
                );
                alert("수정 중 오류가 발생했습니다.");
            })
            .finally(() => {
                setSaving(false);
            });
    };

    /** 1. 로그인 사용자 정보 조회 */
    useEffect(() => {
        if (!token) return;

        axios
            .get(`${API}/userinf`, { headers })
            .then((res) => {
                const data = res.data || {};

                setUserInfo({
                    regId: data.regId || data.userId || data.uid || "",
                    regNM: data.regNM || data.userName || "",
                    aboveIdx: data.aboveIdx || "",
                    aboveNM: data.aboveContractor?.aboveNM || data.aboveNM || "",
                    fldIdx: data.fldIdx || "",
                    fldNM: data.field?.fldNM || data.fldNM || "",
                });
            })
            .catch((err) => {
                console.error("userinf error:", err?.response?.status, err?.response?.data);
            });
    }, [API, token]);

    /** 2. 상위업체 기준 업체 목록 조회 */
    useEffect(() => {
        if (!userInfo.aboveIdx) return;

        axios
            .get(`${API}/companyListByAbove`, {
                params: { aboveIdx: userInfo.aboveIdx },
                headers,
            })
            .then((res) => {
                setCompanyList(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                setCompanyList([]);
            });
    }, [API, userInfo.aboveIdx]);

    /** 3. 현장 기준 라인 목록 조회 */
    useEffect(() => {
        if (!userInfo.fldIdx) return;

        axios
            .get(`${API}/lineList`, {
                params: { fldIdx: userInfo.fldIdx },
                headers,
            })
            .then((res) => {
                setLines(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => {
                setLines([]);
            });
    }, [API, userInfo.fldIdx]);

    /** 4. 수정 대상 상세 정보 조회 */
    useEffect(() => {
        if (!diagIdx) return;

        setLoadingPage(true);

        axios
            .get(`${API}/mydetaillist`, {
                params: { diagIdx },
                headers,
            })
            .then((res) => {
                const data = res.data || {};

                const placeParts = String(data.caughtPlace || "").split(" / ");

                const engineerIdx = String(data.subcontractorEngnrIdx || "");
                const engineerNM = String(data.subcontractorEngnrNM || "");

                const supervisorIdx = String(data.supervisorIdx || "");
                const supervisorName = String(data.supervisorName || data.supervisorNM || "");

                const isSamePerson =
                    engineerIdx &&
                    supervisorIdx &&
                    engineerIdx === supervisorIdx;

                setForm({
                    subcontractorIdx: String(data.subcontractorIdx || ""),
                    subcontractorNM: String(data.subcontractorNM || ""),

                    // 기술인 idx와 작업지휘자 idx가 같으면 기술인 기본값 비움
                    name: isSamePerson ? "" : engineerNM,
                    subcontractorEngnrIdx: isSamePerson ? "" : engineerIdx,
                    subcontractorEngnrNM: isSamePerson ? "" : engineerNM,

                    // 작업지휘자만 유지
                    supervisorName: supervisorName,
                    supervisorIdx: supervisorIdx,
                    supervisorNM: supervisorName,

                    lineName: placeParts[0] || "",
                    floor: placeParts[1] || "",
                    detailPlace: placeParts.slice(2).join(" / ") || "",
                    caughtDT: data.caughtDT ? toLocalDateTimeString(data.caughtDT) : "",

                    evlIdx: String(data.evlIdx || ""),
                    evlClass: String(data.evlClass || ""),
                    evlCategory: String(data.evlCategory || ""),
                    increasePT: String(data.increasePT ?? ""),
                    deductedPT: String(data.deductedPT ?? ""),
                    totalIncreasePT: String(data.totalIncreasePT ?? ""),
                    totalDeductedPT: String(data.totalDeductedPT ?? ""),

                    equipmentName: String(data.equipmentName || ""),
                    actionDescription: String(data.actionDescription || ""),
                    caughtNote: String(data.caughtNote || ""),
                });
            })
            .catch((err) => {
                console.error(
                    "mydetaillist error:",
                    err?.response?.status,
                    err?.response?.data
                );
                alert("수정할 데이터를 불러오지 못했습니다.");
                navigate(-1);
            })
            .finally(() => {
                setLoadingPage(false);
            });
    }, [API, diagIdx, navigate]);

    /** 5. 분류 선택 시 내용 목록 조회 */
    useEffect(() => {
        if (!classEndpoint || !form.evlIdx) return;

        setLoading((prev) => ({ ...prev, classes: true }));

        axios
            .get(classEndpoint, {
                params: { evlIdx: Number(form.evlIdx) },
                headers,
            })
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : res.data?.list ?? [];
                setEvaluationClassList(list);
            })
            .catch(() => {
                setEvaluationClassList([]);
            })
            .finally(() => {
                setLoading((prev) => ({ ...prev, classes: false }));
            });
    }, [classEndpoint, form.evlIdx]);

    /** 6. 내용 목록이 바뀌면 각 내용의 세부사항 조회 */
    useEffect(() => {
        if (!evaluationClassList.length) return;

        setLoading((prev) => ({ ...prev, categories: true }));

        Promise.all(
            evaluationClassList.map((cls) =>
                axios
                    .get(`${API}/evaluationCategoryList`, {
                        params: { classIdx: cls.classIdx },
                        headers,
                    })
                    .then((res) => {
                        return {
                            classIdx: cls.classIdx,
                            data: Array.isArray(res.data) ? res.data : res.data?.list ?? [],
                        };
                    })
                    .catch(() => {
                        return {
                            classIdx: cls.classIdx,
                            data: [],
                        };
                    })
            )
        )
            .then((rows) => {
                const result = {};

                rows.forEach((row) => {
                    result[row.classIdx] = row.data;
                });

                setEvaluationCategoryByClass(result);
            })
            .finally(() => {
                setLoading((prev) => ({ ...prev, categories: false }));
            });
    }, [API, evaluationClassList]);

    /** 7. 세부사항 목록이 바뀌면 점수 조회 */
    useEffect(() => {
        if (!pointEndpoint) return;

        const allCategories = Object.values(evaluationCategoryByClass).flat();
        if (!allCategories.length) return;

        setLoading((prev) => ({ ...prev, points: true }));

        const uniqueCategoryIdxs = [
            ...new Set(
                allCategories
                    .map((item) => item.categoryIdx)
                    .filter((value) => value != null)
            ),
        ];

        Promise.all(
            uniqueCategoryIdxs.map((categoryIdx) =>
                axios
                    .get(pointEndpoint, {
                        params: { categoryIdx },
                        headers,
                    })
                    .then((res) => {
                        return {
                            categoryIdx,
                            point:
                                res.data?.categoryPPoint ??
                                res.data?.categoryMPoint ??
                                null,
                        };
                    })
                    .catch(() => {
                        return {
                            categoryIdx,
                            point: null,
                        };
                    })
            )
        )
            .then((rows) => {
                const pointMap = {};

                rows.forEach((row) => {
                    pointMap[row.categoryIdx] = row.point;
                });

                setPointByCategoryIdx(pointMap);
            })
            .finally(() => {
                setLoading((prev) => ({ ...prev, points: false }));
            });
    }, [pointEndpoint, evaluationCategoryByClass]);

    /** 현재 선택된 내용에 해당하는 세부사항 목록 */
    const detailList = form.evlClass
        ? evaluationCategoryByClass[Number(form.evlClass)] || []
        : [];

    /** 표시용 점수 */
    const displayPoint = isPlus
        ? Number(form.totalIncreasePT || form.increasePT || 0)
        : Number(form.totalDeductedPT || form.deductedPT || 0);

    /** + / - 붙인 표시용 문자열 */
    const signedPointText = displayPoint
        ? isPlus
            ? `+${displayPoint}`
            : `-${displayPoint}`
        : "";

    /** 최초 페이지 로딩 화면 */
    if (loadingPage) {
        return (
            <div className="page">
                <div className="vr_mobile">
                    <MobileHeader onPrev={onPrev || (() => navigate(-1))} />
                    <main className="main">
                        <div className="edit_empty">불러오는 중...</div>
                    </main>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="vr_mobile">
                <MobileHeader onPrev={onPrev || (() => navigate(-1))} />

                <main className="main">
                    <h1 className="title">점검 내역 수정</h1>
                    <p className="sub_title">등록된 점검 정보를 수정할 수 있습니다.</p>

                    <form className="vr_form" onSubmit={handleSubmit}>
                        <section className="vr_card">
                            {/* 대상자 정보 */}
                            <div className="vr_section">
                                <div className="vr_section_head">
                                    <h2>대상자 정보</h2>
                                    <span>기본 정보</span>
                                </div>

                                <div className="vr_field">
                                    <label>업체명 <em>*</em></label>
                                    <select
                                        value={form.subcontractorIdx}
                                        onChange={(e) => handleCompanyChange(e.target.value)}
                                        required
                                    >
                                        <option value="">업체 선택</option>
                                        {companyList.map((company) => (
                                            <option
                                                key={company.underIdx}
                                                value={String(company.underIdx)}
                                            >
                                                {company.underNM}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="vr_field">
                                    <label>기술인 <em>*</em></label>

                                    <div className="person-input-wrap">
                                        <input
                                            type="text"
                                            value={form.name}
                                            placeholder="이름 입력"
                                            onChange={(e) => {
                                                handleChange("name", e.target.value);
                                                setSearchResults([]);

                                                setForm((prev) => ({
                                                    ...prev,
                                                    subcontractorEngnrIdx: "",
                                                    subcontractorEngnrNM: "",
                                                }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    searchPersonsByName();
                                                }
                                            }}
                                            required
                                        />

                                        <div className="vr btn">
                                            <button
                                                type="button"
                                                onClick={searchPersonsByName}
                                                disabled={
                                                    !form.name.trim() ||
                                                    !form.subcontractorIdx ||
                                                    searchLoading
                                                }
                                            >
                                                {searchLoading ? "조회중…" : "검색"}
                                            </button>
                                        </div>
                                    </div>

                                    {searchResults.length > 0 && (
                                        <select
                                            value={form.subcontractorEngnrIdx}
                                            onChange={(e) => {
                                                const selected = searchResults.find(
                                                    (item) =>
                                                        String(item.engnrIdx ?? item.uid) === e.target.value
                                                );

                                                if (!selected) return;

                                                setForm((prev) => ({
                                                    ...prev,
                                                    subcontractorEngnrIdx: String(
                                                        selected.engnrIdx ?? selected.uid
                                                    ),
                                                    subcontractorEngnrNM:
                                                        selected.engnrNM ?? selected.name ?? "",
                                                    name: selected.engnrNM ?? selected.name ?? "",
                                                }));
                                            }}
                                        >
                                            <option value="">검색 결과 선택</option>

                                            {searchResults.map((person) => (
                                                <option
                                                    key={person.engnrIdx ?? person.uid}
                                                    value={String(person.engnrIdx ?? person.uid)}
                                                >
                                                    {`${person.engnrNM ?? person.name ?? ""} / 직무: ${dutyLabel(
                                                        person.engnrDuty ?? person.position ?? ""
                                                    )} / 생년월일: ${formatBirth(
                                                        person.engnrBirthDt ?? person.birth ?? person.birthdt
                                                    )}`}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div className="vr_field">
                                    <label>작업지휘자</label>

                                    <div className="person-input-wrap">
                                        <input
                                            type="text"
                                            value={form.supervisorName}
                                            placeholder="작업지휘자 이름 입력"
                                            onChange={(e) => {
                                                handleChange("supervisorName", e.target.value);
                                                setSupervisorResults([]);

                                                setForm((prev) => ({
                                                    ...prev,
                                                    supervisorIdx: "",
                                                    supervisorNM: "",
                                                }));
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    searchSupervisorsByName();
                                                }
                                            }}
                                        />

                                        <div className="vr btn">
                                            <button
                                                type="button"
                                                onClick={searchSupervisorsByName}
                                                disabled={
                                                    !form.supervisorName.trim() ||
                                                    !form.subcontractorIdx ||
                                                    supervisorSearchLoading
                                                }
                                            >
                                                {supervisorSearchLoading ? "조회중…" : "검색"}
                                            </button>
                                        </div>
                                    </div>

                                    {supervisorResults.length > 0 && (
                                        <select
                                            value={form.supervisorIdx}
                                            onChange={(e) => {
                                                const selected = supervisorResults.find(
                                                    (item) => String(item.engnrIdx ?? item.uid) === e.target.value
                                                );

                                                if (!selected) return;

                                                setForm((prev) => ({
                                                    ...prev,
                                                    supervisorIdx: String(selected.engnrIdx ?? selected.uid),
                                                    supervisorNM: selected.engnrNM ?? selected.name ?? "",
                                                    supervisorName: selected.engnrNM ?? selected.name ?? "",
                                                }));
                                            }}
                                        >
                                            <option value="">검색 결과 선택</option>

                                            {supervisorResults.map((person) => (
                                                <option
                                                    key={person.engnrIdx ?? person.uid}
                                                    value={String(person.engnrIdx ?? person.uid)}
                                                >
                                                    {`${person.engnrNM ?? person.name ?? ""} / 직무: ${dutyLabel(
                                                        person.engnrDuty ?? person.position ?? ""
                                                    )} / 생년월일: ${formatBirth(
                                                        person.engnrBirthDt ?? person.birth ?? person.birthdt
                                                    )}`}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            </div>

                            {/* 위치 정보 */}
                            <div className="vr_section">
                                <div className="vr_section_head">
                                    <h2>위치 정보</h2>
                                    <span>점검 위치</span>
                                </div>

                                <div className="vr_field">
                                    <label>점검 라인 <em>*</em></label>
                                    <select
                                        value={form.lineName}
                                        onChange={(e) => handleChange("lineName", e.target.value)}
                                        required
                                    >
                                        <option value="">점검 라인 선택</option>

                                        {lines.map((line, index) => {
                                            const lineValue = String(line.lineName ?? line.lineNM ?? "");

                                            return (
                                                <option
                                                    key={line.lineUid ?? index}
                                                    value={lineValue}
                                                >
                                                    {line.lineName ?? line.lineNM}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>

                                <div className="vr_field">
                                    <label>세부 위치 <em>*</em></label>
                                    <input
                                        type="text"
                                        value={form.detailPlace}
                                        onChange={(e) =>
                                            handleChange("detailPlace", e.target.value)
                                        }
                                        required
                                    />
                                </div>

                                <div className="vr_field">
                                    <label>층 <em>*</em></label>
                                    <select
                                        value={form.floor}
                                        onChange={(e) => handleChange("floor", e.target.value)}
                                        required
                                    >
                                        <option value="">층 선택</option>
                                        {floorOptions
                                            .filter(Boolean)
                                            .map((floor) => (
                                                <option key={floor} value={floor}>
                                                    {floor}
                                                </option>
                                            ))}
                                    </select>
                                </div>
                            </div>
                            <div className="vr_section">
                                <div className="vr_section_head">
                                    <h2>설비 정보</h2>
                                    <span>설비 정보</span>
                                </div>

                                <div className="vr_field">
                                    <label>설비명 <em>*</em></label>
                                    <input
                                        type="text"
                                        value={form.equipmentName}
                                        onChange={(e) => handleChange("equipmentName", e.target.value)}
                                        placeholder="예: 고소작업대, 용접기, 사다리"
                                        required
                                    />
                                </div>
                            </div>

                            {/* 발생 일시 */}
                            <div className="vr_section">
                                <div className="vr_section_head">
                                    <h2>발생 일시</h2>
                                    <span>기록 시간</span>
                                </div>

                                <div className="vr_field">
                                    <label>발생 일시 <em>*</em></label>
                                    <DatePicker
                                        selected={parseDateTimeLocal(form.caughtDT)}
                                        onChange={(date) =>
                                            handleChange("caughtDT", formatDateTimeLocal(date))
                                        }
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={10}
                                        dateFormat="yyyy-MM-dd HH:mm"
                                        placeholderText="발생 일시 선택"
                                        locale="ko"
                                        className="vr_custom_datepicker"
                                        calendarClassName="vr_custom_calendar"
                                        popperClassName="vr_custom_popper"
                                    />
                                </div>
                            </div>

                            {/* 내용 정보 */}
                            <div className="vr_section">
                                <div className="vr_section_head">
                                    <h2>내용</h2>
                                    <span>상세 정보</span>
                                </div>

                                <div className="vr_field">
                                    <label>분류 <em>*</em></label>
                                    <select
                                        value={form.evlIdx}
                                        onChange={(e) => handleEvlIdxChange(e.target.value)}
                                        required
                                    >
                                        <option value="">선택</option>
                                        <option value="17">우수</option>
                                        <option value="18">불합리</option>
                                    </select>
                                </div>

                                <div className="vr_field">
                                    <label>내용 <em>*</em></label>
                                    <select
                                        value={form.evlClass}
                                        onChange={(e) => handleEvalClassChange(e.target.value)}
                                        disabled={!form.evlIdx}
                                        required
                                    >
                                        <option value="">선택</option>
                                        {evaluationClassList.map((cls) => (
                                            <option key={cls.classIdx} value={String(cls.classIdx)}>
                                                {cls.classNM}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="vr_field">
                                    <label>세부사항 <em>*</em></label>
                                    <select
                                        value={form.evlCategory}
                                        onChange={(e) => handleEvalCategoryChange(e.target.value)}
                                        disabled={!form.evlClass}
                                        required
                                    >
                                        <option value="">선택</option>
                                        {detailList.map((cat) => (
                                            <option
                                                key={cat.categoryIdx}
                                                value={String(cat.categoryIdx)}
                                            >
                                                {cat.categoryNM}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="vr_field">
                                    <label>점수 <em>*</em></label>
                                    <input type="text" value={signedPointText} readOnly />
                                </div>

                                <div className="vr_field">
                                    <label>상세내용 <em>*</em></label>
                                    <textarea
                                        value={form.caughtNote}
                                        onChange={(e) => handleChange("caughtNote", e.target.value)}
                                        rows={5}
                                        required
                                    />
                                </div>
                                <div className="vr_field">
                                    <label>조치내용 <em>*</em></label>
                                    <textarea
                                        value={form.actionDescription}
                                        onChange={(e) => handleChange("actionDescription", e.target.value)}
                                        rows={4}
                                        placeholder="예: 사용 중지 조치, 안전커버 설치, 작업 전 교육 실시"
                                        required
                                    />
                                </div>
                            </div>
                        </section>

                        <div className="btn edit">
                            <button
                                type="button"
                                className="vr_add_btn"
                                onClick={() => navigate(-1)}
                            >
                                취소
                            </button>

                            <button
                                type="submit"
                                disabled={
                                    loading.classes ||
                                    loading.categories ||
                                    loading.points ||
                                    saving
                                }
                            >
                                {saving ? "수정중..." : "수정"}
                            </button>
                        </div>
                    </form>
                </main>
            </div>
        </div>
    );
}